import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Users, CalendarClock, Mail, RefreshCw, Loader2, ShieldAlert, Download, ShieldCheck, Upload, FileText, Search, ChevronDown, ChevronUp, Newspaper, ReceiptText } from "lucide-react";
import AdminArticles from "@/pages/AdminArticles";
import AdminQuotes from "@/pages/AdminQuotes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

const TABS = [
  { key: "leads", label: "Leads", icon: Users, endpoint: "/leads" },
  { key: "consultations", label: "Consultations", icon: CalendarClock, endpoint: "/consultations" },
  { key: "subscribers", label: "Email subscribers", icon: Mail, endpoint: "/sequence/enrollments" },
];

const fmt = (d) => (d ? new Date(d).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—");

const CSV_COLUMNS = {
  leads: ["name", "email", "goal", "current_role", "message", "created_at"],
  consultations: ["full_name", "email", "current_role", "target_role", "service_interest", "contact_method", "notes", "created_at"],
  subscribers: ["first_name", "email", "status", "next_step_index", "next_send_at", "enrolled_at"],
};

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

function exportCsv(tab, rows) {
  const cols = CSV_COLUMNS[tab];
  const lines = [cols.join(","), ...rows.map((r) => cols.map((c) => csvCell(r[c])).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function PackUpload() {
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadStatus = () => http.get("/client-protection/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { loadStatus(); }, []);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.info("Choose a PDF file first.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await http.post("/admin/client-protection", fd);
      toast.success("Client Protection Pack uploaded — it's now live on the website.");
      fileRef.current.value = "";
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="admin-pack-panel" className="mt-8 border border-[#27272A] bg-[#141414] p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="tracking-label text-[10px] uppercase font-body font-bold text-[#DFFF00] flex items-center gap-2 mb-1">
            <ShieldCheck size={13} /> Client Protection Pack
          </p>
          <p data-testid="admin-pack-status" className="font-body text-sm text-[#A1A1AA]">
            {status?.available
              ? `Live on the website: ${status.filename} (uploaded ${fmt(status.uploaded_at)})`
              : "No PDF uploaded yet — the website shows a placeholder note until you upload one."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,.pdf"
            data-testid="admin-pack-file-input"
            className="font-body text-sm text-[#A1A1AA] file:mr-3 file:rounded-full file:border-0 file:bg-[#27272A] file:text-[#F8F8F5] file:px-4 file:py-2 file:font-body file:text-sm file:cursor-pointer"
          />
          <button
            data-testid="admin-pack-upload-btn"
            onClick={upload}
            disabled={uploading}
            className="rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold text-sm px-5 py-2.5 inline-flex items-center gap-2 hover:bg-[#C8E600] transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {status?.available ? "Replace PDF" : "Upload PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CvStatusBadge({ status }) {
  if (status === "retained") {
    return <span data-testid="cv-status-badge" className="inline-flex items-center gap-1.5 text-[11px] font-body font-bold uppercase tracking-wider text-[#DFFF00] border border-[#DFFF00]/40 px-2.5 py-1">CV retained</span>;
  }
  if (status === "failed") {
    return <span data-testid="cv-status-badge" className="inline-flex items-center gap-1.5 text-[11px] font-body font-bold uppercase tracking-wider text-red-400 border border-red-900/60 px-2.5 py-1">CV not saved</span>;
  }
  return <span data-testid="cv-status-badge" className="inline-flex items-center gap-1.5 text-[11px] font-body font-bold uppercase tracking-wider text-[#52525B] border border-[#27272A] px-2.5 py-1">{status === "none" ? "Pasted text — no file" : "No file on record"}</span>;
}

function SubmissionRow({ row, onDownload, downloading }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="submission-row" className="border border-[#27272A] p-5">
      <div className="grid md:grid-cols-5 gap-3 items-start">
        <Field label="Name" value={row.lead_first_name || "—"} />
        <Field label="Email" value={row.lead_email || "—"} link={row.lead_email ? `mailto:${row.lead_email}` : undefined} />
        <Field label="Score" value={`${row.overall_score}/100 · ${row.band || "—"}`} />
        <Field label="Submitted" value={fmt(row.created_at)} />
        <div className="flex flex-col items-start gap-2">
          <CvStatusBadge status={row.cv_file_status} />
          {row.cv_file_status === "retained" && (
            <button
              data-testid="submission-download-btn"
              onClick={() => onDownload(row)}
              disabled={downloading === row.id}
              className="inline-flex items-center gap-1.5 font-body text-sm font-semibold text-[#DFFF00] hover:underline underline-offset-4 disabled:opacity-60"
            >
              {downloading === row.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Download original CV
            </button>
          )}
        </div>
      </div>
      <button
        data-testid="submission-toggle-details"
        onClick={() => setOpen((o) => !o)}
        className="mt-3 inline-flex items-center gap-1.5 font-body text-xs text-[#52525B] hover:text-[#DFFF00] transition-colors"
      >
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {open ? "Hide assessment" : "View assessment"} · ID {row.id}
      </button>
      {open && (
        <div data-testid="submission-details" className="mt-4 pt-4 border-t border-[#27272A]/60 space-y-3 text-sm font-body text-[#A1A1AA]">
          {row.cv_file?.original_filename && (
            <p><b className="text-[#52525B]">File:</b> {row.cv_file.original_filename} ({Math.round((row.cv_file.size || 0) / 1024)} KB)</p>
          )}
          {row.headline_observation && <p><b className="text-[#52525B]">Observation:</b> {row.headline_observation}</p>}
          {row.top_action && <p><b className="text-[#52525B]">Top action:</b> {row.top_action}</p>}
          {Array.isArray(row.categories) && (
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
              {row.categories.map((c) => (
                <p key={c.name} className="flex justify-between gap-4"><span>{c.name}</span><span className="text-[#F8F8F5] tabular-nums">{c.score}/100</span></p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SubmissionsPanel() {
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("analysis") || "");
  const [date, setDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const reqSeq = useRef(0);

  const load = useCallback(async (query, dateVal) => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const r = await http.get("/admin/submissions", { params: { q: query, date: dateVal } });
      if (seq !== reqSeq.current) return;
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      if (seq !== reqSeq.current) return;
      toast.error("Could not load submissions.");
      setRows([]);
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(q, date); /* eslint-disable-next-line */ }, []);

  const download = async (row) => {
    setDownloading(row.id);
    try {
      const r = await http.get(`/admin/submissions/${row.id}/cv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = row.cv_file?.original_filename || `cv-${row.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "No CV file was saved for this submission." : "Could not download this CV.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div data-testid="admin-submissions" className="mt-8">
      <form
        onSubmit={(e) => { e.preventDefault(); load(q, date); }}
        className="flex flex-wrap items-center gap-3"
      >
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]" />
          <input
            data-testid="submissions-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email or analysis ID"
            className="w-full bg-[#141414] border border-[#27272A] font-body text-sm text-[#F8F8F5] pl-11 pr-4 py-3 focus:border-[#DFFF00] outline-none transition-colors"
          />
        </div>
        <input
          data-testid="submissions-date-input"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="bg-[#141414] border border-[#27272A] font-body text-sm text-[#A1A1AA] px-4 py-3 focus:border-[#DFFF00] outline-none transition-colors"
        />
        <button
          type="submit"
          data-testid="submissions-search-btn"
          className="rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold text-sm px-6 py-3 hover:bg-[#C8E600] transition-colors"
        >
          Search
        </button>
        {(q || date) && (
          <button
            type="button"
            data-testid="submissions-clear-btn"
            onClick={() => { setQ(""); setDate(""); load("", ""); }}
            className="font-body text-sm text-[#52525B] hover:text-[#DFFF00] transition-colors"
          >
            Clear
          </button>
        )}
      </form>

      <p data-testid="submissions-count" className="font-body text-sm text-[#52525B] mt-6 mb-4">
        {loading ? "Loading…" : `${rows.length} submission(s)`}
      </p>
      {!loading && rows.length === 0 ? (
        <p className="font-body text-[#A1A1AA]">No submissions found.</p>
      ) : (
        <div className="space-y-3" data-testid="submissions-rows">
          {rows.map((row) => (
            <SubmissionRow key={row.id} row={row} onDownload={download} downloading={downloading} />
          ))}
        </div>
      )}
    </div>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function login() {  const redirectUrl = window.location.origin + "/admin";
  if (window.location.search) sessionStorage.setItem("admin_return_search", window.location.search);
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking, obj = user, false = not authed
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["submissions", "articles", "quotes"].includes(t) ? t : "leads";
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const errorParam = new URLSearchParams(window.location.search).get("error");

  useEffect(() => {
    http.get("/auth/me").then((r) => setAuthed(r.data)).catch(() => setAuthed(false));
  }, []);

  const loadTab = useCallback(async (key) => {
    const t = TABS.find((x) => x.key === key);
    setLoading(true);
    try {
      const r = await http.get(t.endpoint);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Could not load data.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed && !["submissions", "articles", "quotes"].includes(tab)) loadTab(tab);
  }, [authed, tab, loadTab]);

  const logout = async () => {
    try { await http.post("/auth/logout"); } catch (e) { /* ignore */ }
    window.location.replace("/admin");
  };

  // ---- Loading state ----
  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F8F8F5] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#DFFF00]" size={28} />
      </div>
    );
  }

  // ---- Login screen ----
  if (!authed) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-[#0A0A0A] text-[#F8F8F5] flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <Link to="/" className="font-display text-xl font-bold tracking-tighter">
            Barrett Career <span className="text-[#DFFF00]">Brand</span>
          </Link>
          <h1 className="mt-10 font-display text-4xl font-bold tracking-tighter">Admin</h1>
          <p className="mt-3 font-body text-[#A1A1AA]">Sign in to view leads, consultations and subscribers.</p>
          {errorParam === "unauthorised" && (
            <div data-testid="admin-error" className="mt-6 flex items-center gap-2 justify-center text-sm text-red-400 border border-red-900/50 bg-red-950/30 px-4 py-3">
              <ShieldAlert size={16} /> This Google account is not authorised for admin access.
            </div>
          )}
          {errorParam === "failed" && (
            <div data-testid="admin-error" className="mt-6 text-sm text-red-400 border border-red-900/50 bg-red-950/30 px-4 py-3">
              Sign-in failed. Please try again.
            </div>
          )}
          <button
            data-testid="admin-login-btn"
            onClick={login}
            className="mt-8 w-full rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-7 py-4 hover:bg-[#C8E600] transition-colors"
          >
            Continue with Google
          </button>
          <Link to="/" className="mt-6 inline-block font-body text-sm text-[#52525B] hover:text-[#DFFF00] transition-colors">← Back to site</Link>
        </div>
      </div>
    );
  }

  // ---- Dashboard ----
  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-[#F8F8F5]">
      <header className="border-b border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="font-display text-lg font-bold tracking-tighter">
            Barrett Career <span className="text-[#DFFF00]">Brand</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="font-body text-sm text-[#A1A1AA] hidden sm:block">{authed.email}</span>
            <button
              data-testid="admin-logout-btn" onClick={logout}
              className="flex items-center gap-2 rounded-full border border-[#27272A] px-4 py-2 font-body text-sm text-[#F8F8F5] hover:border-[#DFFF00] transition-colors"
            >
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  data-testid={`admin-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-body text-sm transition-colors ${
                    tab === t.key ? "bg-[#DFFF00] text-[#0A0A0A] font-semibold" : "border border-[#27272A] text-[#A1A1AA] hover:text-[#F8F8F5]"
                  }`}
                >
                  <Icon size={15} /> {t.label}
                </button>
              );
            })}
            <button
              data-testid="admin-tab-submissions"
              onClick={() => setTab("submissions")}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-body text-sm transition-colors ${
                tab === "submissions" ? "bg-[#DFFF00] text-[#0A0A0A] font-semibold" : "border border-[#27272A] text-[#A1A1AA] hover:text-[#F8F8F5]"
              }`}
            >
              <FileText size={15} /> CV Submissions
            </button>
            <button
              data-testid="admin-tab-quotes"
              onClick={() => setTab("quotes")}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-body text-sm transition-colors ${
                tab === "quotes" ? "bg-[#DFFF00] text-[#0A0A0A] font-semibold" : "border border-[#27272A] text-[#A1A1AA] hover:text-[#F8F8F5]"
              }`}
            >
              <ReceiptText size={15} /> Quotes & orders
            </button>
            <button
              data-testid="admin-tab-articles"
              onClick={() => setTab("articles")}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-body text-sm transition-colors ${
                tab === "articles" ? "bg-[#DFFF00] text-[#0A0A0A] font-semibold" : "border border-[#27272A] text-[#A1A1AA] hover:text-[#F8F8F5]"
              }`}
            >
              <Newspaper size={15} /> Articles
            </button>
          </div>
          {!["submissions", "articles", "quotes"].includes(tab) && (
          <div className="flex items-center gap-5">
            <button
              data-testid="admin-export-btn"
              onClick={() => (rows.length ? exportCsv(tab, rows) : toast.info("Nothing to export yet."))}
              className="flex items-center gap-2 font-body text-sm text-[#A1A1AA] hover:text-[#DFFF00] transition-colors"
            >
              <Download size={15} /> Export CSV
            </button>
            <button
              data-testid="admin-refresh-btn" onClick={() => loadTab(tab)}
              className="flex items-center gap-2 font-body text-sm text-[#A1A1AA] hover:text-[#DFFF00] transition-colors"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
          )}
        </div>

        <PackUpload />

        {tab === "submissions" ? (
          <SubmissionsPanel />
        ) : tab === "articles" ? (
          <AdminArticles />
        ) : tab === "quotes" ? (
          <AdminQuotes />
        ) : (
        <div className="mt-8">
          <p data-testid="admin-count" className="font-body text-sm text-[#52525B] mb-4">
            {loading ? "Loading…" : `${rows.length} ${tab === "subscribers" ? "subscriber(s)" : "record(s)"}`}
          </p>
          {!loading && rows.length === 0 ? (
            <p className="font-body text-[#A1A1AA]">Nothing here yet.</p>
          ) : (
            <div className="space-y-3" data-testid="admin-rows">
              {rows.map((row, i) => (
                <Row key={row.id || row.user_id || i} tab={tab} row={row} />
              ))}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

function Row({ tab, row }) {
  if (tab === "leads") {
    return (
      <div className="border border-[#27272A] p-5 grid md:grid-cols-4 gap-3">
        <Field label="Name" value={row.name} />
        <Field label="Email" value={row.email} link={`mailto:${row.email}`} />
        <Field label="Goal" value={row.goal} />
        <Field label="Received" value={fmt(row.created_at)} />
        {(row.current_role || row.message) && (
          <div className="md:col-span-4 text-sm text-[#A1A1AA] font-body pt-2 border-t border-[#27272A]/60">
            {row.current_role && <span className="mr-4"><b className="text-[#52525B]">Role:</b> {row.current_role}</span>}
            {row.message && <span><b className="text-[#52525B]">Message:</b> {row.message}</span>}
          </div>
        )}
      </div>
    );
  }
  if (tab === "consultations") {
    return (
      <div className="border border-[#27272A] p-5 grid md:grid-cols-4 gap-3">
        <Field label="Name" value={row.full_name} />
        <Field label="Email" value={row.email} link={`mailto:${row.email}`} />
        <Field label="Service" value={row.service_interest || "—"} />
        <Field label="Received" value={fmt(row.created_at)} />
        <div className="md:col-span-4 text-sm text-[#A1A1AA] font-body pt-2 border-t border-[#27272A]/60">
          {row.current_role && <span className="mr-4"><b className="text-[#52525B]">Current:</b> {row.current_role}</span>}
          {row.target_role && <span className="mr-4"><b className="text-[#52525B]">Target:</b> {row.target_role}</span>}
          {row.contact_method && <span className="mr-4"><b className="text-[#52525B]">Contact:</b> {row.contact_method}</span>}
          {row.notes && <span><b className="text-[#52525B]">Notes:</b> {row.notes}</span>}
        </div>
      </div>
    );
  }
  // subscribers
  return (
    <div className="border border-[#27272A] p-5 grid md:grid-cols-4 gap-3">
      <Field label="Name" value={row.first_name} />
      <Field label="Email" value={row.email} link={`mailto:${row.email}`} />
      <Field label="Status" value={`${row.status} · step ${row.next_step_index}/5`} />
      <Field label="Next send" value={row.next_send_at ? fmt(row.next_send_at) : "Complete"} />
      <div className="md:col-span-4 text-sm text-[#A1A1AA] font-body pt-2 border-t border-[#27272A]/60">
        <b className="text-[#52525B]">Enrolled:</b> {fmt(row.enrolled_at)}
        {Array.isArray(row.sent_steps) && row.sent_steps.length > 0 && (
          <span className="ml-4"><b className="text-[#52525B]">Sent:</b> {row.sent_steps.map((s) => s.key).join(", ")}</span>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, link }) {
  return (
    <div>
      <p className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B]">{label}</p>
      {link ? (
        <a href={link} className="font-body text-[#DFFF00] hover:underline break-all">{value}</a>
      ) : (
        <p className="font-body text-[#F8F8F5] break-words">{value || "—"}</p>
      )}
    </div>
  );
}
